import type { CurriculumTarget } from '@interactive-room';

export type RubricLevelCount = 3 | 4 | 5;

/** Individual = evaluación uniforme; group = ABP con roles. */
export type RubricScope = 'individual' | 'group';

/** Tipo pedagógico de la evaluación (alinea prompts IA y UI). */
export type RubricEvaluationType = 'diagnostica' | 'formativa' | 'sumativa';

export const RUBRIC_EVALUATION_TYPE_OPTIONS: Array<{
  id: RubricEvaluationType;
  label: string;
  hint: string;
}> = [
  {
    id: 'diagnostica',
    label: 'Diagnóstica',
    hint: 'Conocimientos previos y punto de partida',
  },
  {
    id: 'formativa',
    label: 'Formativa',
    hint: 'Proceso, avance y retroalimentación',
  },
  {
    id: 'sumativa',
    label: 'Sumativa',
    hint: 'Logro final y certificación',
  },
];

export function normalizeRubricEvaluationType(
  value?: string | null,
): RubricEvaluationType {
  const raw = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (raw === 'diagnostica' || raw.includes('diagnostic')) return 'diagnostica';
  if (raw === 'sumativa' || raw.includes('sumativ')) return 'sumativa';
  if (raw === 'formativa' || raw.includes('formativ')) return 'formativa';
  return 'formativa';
}

export function rubricEvaluationTypeLabel(value?: string | null): string {
  const type = normalizeRubricEvaluationType(value);
  if (type === 'diagnostica') return 'Evaluación diagnóstica';
  if (type === 'sumativa') return 'Evaluación sumativa';
  return 'Evaluación formativa';
}

export type RubricCriterionCount = number;

/** Rango del stepper / IA en modo individual. */
export const RUBRIC_CRITERION_COUNT_MIN = 3;
export const RUBRIC_CRITERION_COUNT_MAX = 16;

/** Criterios transversales (todos los roles) en rúbrica grupal. */
export type RubricTransversalCriterionCount = 0 | 1 | 2 | 3 | 4;

/** Criterios independientes que recibe CADA rol en rúbrica grupal. */
export type RubricPerRoleCriterionCount = 1 | 2 | 3 | 4 | 5;

/** Rol ABP dentro de una rúbrica (locución, sonido, etc.). */
export interface RubricProjectRole {
  id: string;
  name: string;
  description?: string;
}

export interface RubricLevel {
  id: string;
  name: string;
  points?: number;
}

export interface RubricCriterion {
  id: string;
  name: string;
  description: string;
  /** levelId → descriptor de desempeño en ese nivel */
  cells: Record<string, string>;
  /**
   * IDs de roles a los que aplica este criterio.
   * `['*']` = transversal (todos). Ausente/vacío se normaliza a `['*']`.
   */
  applicableRoleIds?: string[];
  /** Peso relativo (default 1). */
  weight?: number;
  /**
   * Códigos OA alineados a este criterio (metadato UI).
   * No van en la prosa del criterio. Ausente → [].
   */
  oaIds?: string[];
}

/** Estado de avance del proyecto ABP (informa a la IA; no es tracker de hitos). */
export type RubricProjectProgress = 'inicio' | 'en_curso' | 'casi_terminado';


/** OA MINEDUC seleccionado en la rúbrica (soporta multi-selección). */
export interface RubricOaSelection {
  code: string;
  text: string;
  axis?: string;
  /** Asignatura de origen (mono o inter/multi). */
  subjectTag?: string;
  subjectLabel?: string;
}

/**
 * Alcance curricular de la rúbrica.
 * - mono: solo asignatura del curso
 * - interdisciplinar / multidisciplinar: varias asignaturas → OA de las elegidas
 */
export type RubricCurriculumMode = 'mono' | 'interdisciplinar' | 'multidisciplinar';

export const RUBRIC_CURRICULUM_MODE_OPTIONS: Array<{
  id: RubricCurriculumMode;
  label: string;
  hint: string;
}> = [
  {
    id: 'mono',
    label: 'Una asignatura',
    hint: 'Solo la asignatura del curso',
  },
  {
    id: 'interdisciplinar',
    label: 'Interdisciplinar',
    hint: 'Integra saberes entre asignaturas en criterios compartidos',
  },
  {
    id: 'multidisciplinar',
    label: 'Multidisciplinar',
    hint: 'Varias asignaturas en paralelo (criterios por materia y transversales)',
  },
];

export function normalizeRubricCurriculumMode(
  value?: string | null,
): RubricCurriculumMode {
  const raw = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (raw === 'interdisciplinar' || raw.includes('interdisciplin')) return 'interdisciplinar';
  if (raw === 'multidisciplinar' || raw.includes('multidisciplin')) return 'multidisciplinar';
  return 'mono';
}

export interface RubricDraft {
  id: string;
  courseId: string;
  title: string;
  /** Intención evaluativa / brief del docente (guía la IA; no es el texto impreso). */
  objective: string;
  /**
   * Objetivo de la evaluación redactado para imprimir (estudiante/familia).
   * Distinto del brief: la IA lo reformula; no debe copiar el prompt docente.
   */
  evaluationObjective?: string;
  /**
   * Tipo pedagógico (diagnóstica / formativa / sumativa).
   * Ausente se trata como formativa (legacy).
   */
  evaluationType?: RubricEvaluationType;
  /**
   * mono | interdisciplinar | multidisciplinar.
   * Ausente = mono (asignatura del curso).
   */
  curriculumMode?: RubricCurriculumMode;
  /**
   * Asignaturas adicionales (además de la del curso) cuando curriculumMode ≠ mono.
   * Tags canónicos: matematica, musica, lenguaje, …
   */
  extraSubjectTags?: string[];
  /**
   * Niveles curriculares cubiertos por la evaluación (diagnóstica multi-nivel).
   * Vacío / ausente = solo el nivel del curso.
   */
  targetLevels?: string[];
  /** OA seleccionados (multi). Preferir sobre oaCode/oaText. */
  oas?: RubricOaSelection[];
  /** @deprecated Usar oas[0].code — se mantiene por compatibilidad. */
  oaCode?: string;
  /** @deprecated Usar oas[0].text — se mantiene por compatibilidad. */
  oaText?: string;
  /** Eje + OA primario (+ OAH/OAA opcionales) para alinear la generación IA. */
  curriculumTarget?: CurriculumTarget;
  /**
   * individual = sin capa de roles; group = proyecto ABP con roles.
   * Ausente se trata como individual (legacy).
   */
  scope?: RubricScope;
  /** Cantidad de criterios pedida a la IA en modo individual (default 4). */
  criterionCount?: RubricCriterionCount;
  /**
   * Rúbrica grupal: criterios transversales (applicableRoles "*").
   * Independiente de perRoleCriterionCount.
   */
  transversalCriterionCount?: RubricTransversalCriterionCount;
  /**
   * Rúbrica grupal: cantidad de criterios propios de CADA rol
   * (no un cupo compartido entre roles).
   */
  perRoleCriterionCount?: RubricPerRoleCriterionCount;
  /** Roles del proyecto ABP. Solo relevantes si scope === 'group'. */
  projectRoles?: RubricProjectRole[];
  /**
   * Etapa del proyecto/ABP al momento de generar.
   * Ausente se trata como 'inicio' (comportamiento legacy).
   */
  estadoProyecto?: RubricProjectProgress;
  /** Qué ya se hizo — relevante si estadoProyecto es en_curso o casi_terminado. */
  avanceDescripcion?: string;
  levelCount: RubricLevelCount;
  levels: RubricLevel[];
  criteria: RubricCriterion[];
  /** true cuando la matriz ya fue generada (IA o manual expandida) */
  hasMatrix: boolean;
  createdAt: number;
  updatedAt: number;
}

export const RUBRIC_ROLE_ALL = '*';

/** Normaliza selección multi/legacy a lista de OA. */
export function resolveRubricOas(
  draft: Pick<RubricDraft, 'oas' | 'oaCode' | 'oaText' | 'curriculumTarget'>,
): RubricOaSelection[] {
  if (Array.isArray(draft.oas) && draft.oas.length > 0) {
    return draft.oas.filter((oa) => oa?.code && oa?.text);
  }
  const code = draft.curriculumTarget?.oaCode || draft.oaCode;
  const text = draft.oaText;
  if (code && text) {
    return [{ code, text, axis: draft.curriculumTarget?.axis }];
  }
  return [];
}

/** Une textos oficiales de OA para intención / prompt (estilo Aula). */
export function joinRubricOaTexts(oas: RubricOaSelection[]): string {
  return oas
    .map((oa) => oa.text.trim())
    .filter(Boolean)
    .join(' | ');
}

/** Tags de asignatura activos para catálogo OA (curso + extras en inter/multi). */
export function resolveRubricSubjectTags(
  draft: Pick<RubricDraft, 'curriculumMode' | 'extraSubjectTags'>,
  courseSubjectTag?: string | null,
  courseAdditionalTags: string[] = [],
): string[] {
  const course = String(courseSubjectTag || '')
    .trim()
    .toLowerCase();
  const courseExtras = (Array.isArray(courseAdditionalTags) ? courseAdditionalTags : [])
    .map((tag) => String(tag || '').trim().toLowerCase())
    .filter((tag) => tag && tag !== course);
  const mode = normalizeRubricCurriculumMode(draft.curriculumMode);
  if (mode === 'mono') return course ? [course] : [];

  const extras = Array.isArray(draft.extraSubjectTags)
    ? draft.extraSubjectTags
        .map((tag) => String(tag || '').trim().toLowerCase())
        .filter((tag) => tag && tag !== course)
    : [];
  return [...new Set([...(course ? [course] : []), ...courseExtras, ...extras])];
}

/**
 * Asignaturas para encabezado de impresión: siempre incluye la principal del curso.
 * Une curso (principal + adicionales) con extras del editor, sin perder la ancla.
 */
export function resolvePrintedSubjectTags(
  draft: Pick<RubricDraft, 'curriculumMode' | 'extraSubjectTags'>,
  courseSubjectTag?: string | null,
  courseAdditionalTags: string[] = [],
): string[] {
  const course = String(courseSubjectTag || '')
    .trim()
    .toLowerCase();
  const courseExtras = (Array.isArray(courseAdditionalTags) ? courseAdditionalTags : [])
    .map((tag) => String(tag || '').trim().toLowerCase())
    .filter((tag) => tag && tag !== course);
  const draftExtras = Array.isArray(draft.extraSubjectTags)
    ? draft.extraSubjectTags
        .map((tag) => String(tag || '').trim().toLowerCase())
        .filter((tag) => tag && tag !== course)
    : [];
  return [...new Set([...(course ? [course] : []), ...courseExtras, ...draftExtras])];
}

/** Niveles curriculares activos (diagnóstica multi-nivel o nivel del curso). */
export function resolveRubricTargetLevels(
  draft: Pick<RubricDraft, 'evaluationType' | 'targetLevels'>,
  courseLevel?: string | null,
): string[] {
  const course = String(courseLevel || '').trim().toLowerCase();
  const fromDraft = Array.isArray(draft.targetLevels)
    ? draft.targetLevels.map((level) => String(level || '').trim().toLowerCase()).filter(Boolean)
    : [];
  if (normalizeRubricEvaluationType(draft.evaluationType) !== 'diagnostica') {
    return course ? [course] : fromDraft.slice(0, 1);
  }
  const unique = [...new Set(fromDraft.length ? fromDraft : course ? [course] : [])];
  return unique;
}

/** Etiquetas legibles de niveles (diagnóstica multi-nivel). */
export function formatRubricTargetLevelsHeader(
  draft: Pick<RubricDraft, 'evaluationType' | 'targetLevels'>,
  courseLevel: string | null | undefined,
  resolveLabel: (levelId: string) => string,
): string {
  return resolveRubricTargetLevels(draft, courseLevel)
    .map((id) => String(resolveLabel(id) || id).trim())
    .filter(Boolean)
    .join(' · ');
}

export function isCrossSubjectCurriculumMode(
  mode?: RubricCurriculumMode | string | null,
): boolean {
  const normalized = normalizeRubricCurriculumMode(mode);
  return normalized === 'interdisciplinar' || normalized === 'multidisciplinar';
}

/**
 * Etiquetas de asignatura para encabezado / prompts (curso + extras en inter/multi).
 * `resolveLabel` mapea tag → nombre legible (p. ej. getSubjectNameByTag).
 */
export function resolveRubricSubjectLabels(
  draft: Pick<RubricDraft, 'curriculumMode' | 'extraSubjectTags'>,
  courseSubjectTag: string | null | undefined,
  resolveLabel: (tag: string) => string,
  courseAdditionalTags: string[] = [],
): string[] {
  return resolveRubricSubjectTags(draft, courseSubjectTag, courseAdditionalTags)
    .map((tag) => String(resolveLabel(tag) || tag).trim())
    .filter(Boolean);
}

/** Texto de encabezado: "Música · Lenguaje · Matemáticas" (siempre con principal del curso). */
export function formatRubricSubjectHeader(
  draft: Pick<RubricDraft, 'curriculumMode' | 'extraSubjectTags'>,
  courseSubjectTag: string | null | undefined,
  resolveLabel: (tag: string) => string,
  fallback = 'Asignatura',
  courseAdditionalTags: string[] = [],
): string {
  const labels = resolvePrintedSubjectTags(draft, courseSubjectTag, courseAdditionalTags)
    .map((tag) => String(resolveLabel(tag) || tag).trim())
    .filter(Boolean);
  if (labels.length) return labels.join(' · ');
  const fallbackLabel = String(fallback || '').trim();
  return fallbackLabel || 'Asignatura';
}

/**
 * ¿La generación/guardado exige al menos un OA?
 * Diagnóstica: nunca (OA opcional).
 * Formativa/sumativa: sí si hay al menos una asignatura con matriz curricular.
 */
export function rubricRequiresCurriculumOa(
  draft: Pick<RubricDraft, 'evaluationType' | 'curriculumMode' | 'extraSubjectTags'>,
  courseSubjectTag: string | null | undefined,
  hasCurriculumFn: (tag: string | null | undefined) => boolean,
  courseAdditionalTags: string[] = [],
): boolean {
  if (normalizeRubricEvaluationType(draft.evaluationType) === 'diagnostica') {
    return false;
  }
  const tags = resolveRubricSubjectTags(draft, courseSubjectTag, courseAdditionalTags);
  if (!tags.length) return hasCurriculumFn(courseSubjectTag);
  return tags.some((tag) => hasCurriculumFn(tag));
}

export function createProjectRoleId(): string {
  return `role_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Patrones que indican un nombre de rol compuesto (dos funciones en uno). */
export const COMPOUND_ROLE_NAME_PATTERN = /\s+y\s+|\s+e\s+|\/|,|;|&/i;

export const COMPOUND_ROLE_NAME_MESSAGE =
  "Los nombres de rol no pueden combinar dos funciones con 'y'/'e'. Si el proyecto necesita ambos, créalos como roles separados y asígnalos juntos a los criterios que correspondan.";

export function isCompoundRoleName(name: string): boolean {
  return COMPOUND_ROLE_NAME_PATTERN.test(String(name || '').trim());
}

/** Normaliza applicableRoleIds: vacío/ausente → ['*']. */
export function normalizeApplicableRoleIds(ids?: string[] | null): string[] {
  if (!Array.isArray(ids) || ids.length === 0) return [RUBRIC_ROLE_ALL];
  const cleaned = ids.map((id) => String(id || '').trim()).filter(Boolean);
  if (!cleaned.length) return [RUBRIC_ROLE_ALL];
  if (cleaned.includes(RUBRIC_ROLE_ALL)) return [RUBRIC_ROLE_ALL];
  return [...new Set(cleaned)];
}

export function normalizeCriterionWeight(weight?: number | null): number {
  const n = Number(weight);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return n;
}

/**
 * Migración / normalización a la carga: rúbricas antiguas sin roles
 * se comportan como evaluación uniforme (criterios transversales).
 */
export function normalizeRubricDraft(draft: RubricDraft): RubricDraft {
  const projectRoles = Array.isArray(draft.projectRoles)
    ? draft.projectRoles
        .map((role) => ({
          id: String(role?.id || '').trim(),
          name: String(role?.name || '').trim(),
          description: role?.description ? String(role.description).trim() : undefined,
        }))
        .filter((role) => role.id && role.name)
    : [];

  const criteria = (draft.criteria || []).map((criterion) => ({
    ...criterion,
    applicableRoleIds: normalizeApplicableRoleIds(criterion.applicableRoleIds),
    weight: normalizeCriterionWeight(criterion.weight),
    oaIds: normalizeCriterionOaIds(criterion.oaIds),
  }));

  // Legacy: rúbricas con roles pero sin scope → grupal.
  const scope: RubricScope =
    draft.scope === 'group' || draft.scope === 'individual'
      ? draft.scope
      : projectRoles.length > 0
        ? 'group'
        : 'individual';
  const criterionCount = normalizeRubricCriterionCount(draft.criterionCount);
  const transversalCriterionCount = normalizeTransversalCriterionCount(
    draft.transversalCriterionCount,
  );
  const perRoleCriterionCount = normalizePerRoleCriterionCount(draft.perRoleCriterionCount);
  const estadoProyecto = normalizeRubricProjectProgress(draft.estadoProyecto);
  const avanceDescripcion =
    estadoProyecto === 'inicio'
      ? ''
      : String(draft.avanceDescripcion || '').trim();
  const evaluationType = normalizeRubricEvaluationType(draft.evaluationType);
  const curriculumMode = normalizeRubricCurriculumMode(draft.curriculumMode);
  const extraSubjectTags =
    curriculumMode === 'mono'
      ? []
      : [
          ...new Set(
            (Array.isArray(draft.extraSubjectTags) ? draft.extraSubjectTags : [])
              .map((tag) => String(tag || '').trim().toLowerCase())
              .filter(Boolean),
          ),
        ];
  const targetLevels =
    evaluationType === 'diagnostica'
      ? [
          ...new Set(
            (Array.isArray(draft.targetLevels) ? draft.targetLevels : [])
              .map((level) => String(level || '').trim().toLowerCase())
              .filter(Boolean),
          ),
        ]
      : [];
  const oas = Array.isArray(draft.oas)
    ? draft.oas
        .filter((oa) => oa?.code && oa?.text)
        .map((oa) => ({
          code: String(oa.code).trim(),
          text: String(oa.text).trim(),
          axis: oa.axis ? String(oa.axis).trim() : undefined,
          subjectTag: oa.subjectTag
            ? String(oa.subjectTag).trim().toLowerCase()
            : undefined,
          subjectLabel: oa.subjectLabel ? String(oa.subjectLabel).trim() : undefined,
        }))
    : draft.oas;
  const evaluationObjective = String(draft.evaluationObjective || '').trim();

  return {
    ...draft,
    scope,
    evaluationType,
    evaluationObjective,
    curriculumMode,
    extraSubjectTags,
    targetLevels,
    oas,
    criterionCount,
    transversalCriterionCount,
    perRoleCriterionCount,
    estadoProyecto,
    avanceDescripcion,
    projectRoles: scope === 'group' ? projectRoles : [],
    criteria:
      scope === 'group'
        ? criteria
        : criteria.map((criterion) => ({
            ...criterion,
            applicableRoleIds: [RUBRIC_ROLE_ALL],
          })),
  };
}

export function normalizeCriterionOaIds(ids?: string[] | null): string[] {
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))];
}

export function normalizeRubricProjectProgress(
  value?: string | null,
): RubricProjectProgress {
  if (value === 'en_curso' || value === 'casi_terminado' || value === 'inicio') return value;
  return 'inicio';
}

export function normalizeRubricCriterionCount(value?: number | null): RubricCriterionCount {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return 4;
  return Math.min(RUBRIC_CRITERION_COUNT_MAX, Math.max(RUBRIC_CRITERION_COUNT_MIN, n));
}

export function normalizeTransversalCriterionCount(
  value?: number | null,
): RubricTransversalCriterionCount {
  const n = Number(value);
  if (n === 0 || n === 1 || n === 2 || n === 3 || n === 4) return n;
  return 2;
}

export function normalizePerRoleCriterionCount(value?: number | null): RubricPerRoleCriterionCount {
  const n = Number(value);
  if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5) return n;
  return 2;
}

/** Total esperado de filas: transversales + (por-rol × roles). */
export function expectedGroupCriterionTotal(
  draft: Pick<
    RubricDraft,
    'transversalCriterionCount' | 'perRoleCriterionCount' | 'projectRoles'
  >,
): number {
  const transversal = normalizeTransversalCriterionCount(draft.transversalCriterionCount);
  const perRole = normalizePerRoleCriterionCount(draft.perRoleCriterionCount);
  const roleCount = Array.isArray(draft.projectRoles) ? draft.projectRoles.length : 0;
  return transversal + perRole * roleCount;
}

export function isGroupRubric(draft: Pick<RubricDraft, 'scope'>): boolean {
  return draft.scope === 'group';
}

/** ¿El criterio aplica a alguno de los roles del estudiante? */
export function criterionAppliesToRoles(
  criterion: Pick<RubricCriterion, 'applicableRoleIds'>,
  studentRoleIds: string[],
): boolean {
  const applicable = normalizeApplicableRoleIds(criterion.applicableRoleIds);
  if (applicable.includes(RUBRIC_ROLE_ALL)) return true;
  if (!studentRoleIds.length) return false;
  return applicable.some((roleId) => studentRoleIds.includes(roleId));
}

/** Criterios visibles para un conjunto de roles (o filtro UI por un rol). */
export function filterCriteriaForRoles(
  criteria: RubricCriterion[],
  roleIds: string[] | 'all',
): RubricCriterion[] {
  if (roleIds === 'all') return criteria;
  return criteria.filter((criterion) => criterionAppliesToRoles(criterion, roleIds));
}

export function getMaxPointsForLevels(levels: RubricLevel[]): number {
  const points = levels.map((level) => Number(level.points)).filter((n) => Number.isFinite(n) && n > 0);
  return points.length ? Math.max(...points) : 1;
}
